import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProfileProvider } from "@/context/ProfileContext";
import { HostTenantProvider, useHostTenant } from "@/context/HostTenantContext";
import { ProtectedRoute, GuestOnlyRoute } from "@/components/RouteGuards";

import MarketingHome from "@/pages/MarketingHome";
import Login from "@/pages/Login";
import SignupBlocked from "@/pages/SignupBlocked";
import AdminDashboard from "@/pages/AdminDashboard";
import StudioDashboard from "@/pages/StudioDashboard";
import StudioCatalogue from "@/pages/StudioCatalogue";
import StudioCatalogueEditor from "@/pages/StudioCatalogueEditor";
import StudioSubscribers from "@/pages/StudioSubscribers";
import StudioPlans from "@/pages/StudioPlans";
import StudioBilling from "@/pages/StudioBilling";
import StudioAnalytics from "@/pages/StudioAnalytics";
import StudioRevenue from "@/pages/StudioRevenue";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminTenantDetail from "@/pages/AdminTenantDetail";
import AdminOnboardWizard from "@/pages/AdminOnboardWizard";
import Storefront from "@/pages/Storefront";
import StorefrontLogin from "@/pages/StorefrontLogin";
import StorefrontSignup from "@/pages/StorefrontSignup";
import StorefrontPlans from "@/pages/StorefrontPlans";
import SubscriberHome from "@/pages/SubscriberHome";
import ContentDetail from "@/pages/ContentDetail";
import Watch from "@/pages/Watch";
import SearchResults from "@/pages/SearchResults";
import MyList from "@/pages/MyList";
import Account from "@/pages/Account";
import AccountBilling from "@/pages/AccountBilling";
import Checkout from "@/pages/Checkout";
import CheckoutMock from "@/pages/CheckoutMock";
import ReelsFeed from "@/pages/ReelsFeed";
import ReelShare from "@/pages/ReelShare";
import SelectProfile from "@/pages/SelectProfile";
import ManageProfiles from "@/pages/ManageProfiles";
import StudioDomains from "@/pages/StudioDomains";
import AdminDomains from "@/pages/AdminDomains";
import AdminUsers from "@/pages/AdminUsers";
import { NotFound, Forbidden } from "@/pages/ErrorPages";
import { DomainNotConnected, DomainLoading } from "@/pages/DomainStates";

function CustomDomainRoutes() {
  // Viewer-facing storefront served at the domain root (no /s/<slug> prefix).
  return (
    <Routes>
      <Route path="/" element={<Storefront />} />
      <Route path="/login" element={<StorefrontLogin />} />
      <Route path="/signup" element={<StorefrontSignup />} />
      <Route path="/title/:contentSlug" element={<ContentDetail />} />
      <Route path="/search" element={<SearchResults />} />
      <Route path="/plans" element={<StorefrontPlans />} />
      <Route path="/home" element={<SubscriberHome />} />
      <Route path="/reels" element={<ReelsFeed />} />
      <Route path="/r/:reelSlug" element={<ReelShare />} />
      <Route path="/select-profile" element={<SelectProfile />} />
      <Route path="/profiles" element={<ManageProfiles />} />
      <Route path="/my-list" element={<MyList />} />
      <Route path="/account" element={<Account />} />
      <Route path="/account/billing" element={<AccountBilling />} />
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/checkout/mock" element={<CheckoutMock />} />
      <Route path="/watch/:contentSlug" element={<Watch />} />
      <Route path="/watch/:contentSlug/:season/:episode" element={<Watch />} />
      <Route path="/forbidden" element={<Forbidden />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function PlatformRoutes() {
  return (
    <Routes>
      <Route path="/" element={<MarketingHome />} />
      <Route path="/login" element={<GuestOnlyRoute><Login /></GuestOnlyRoute>} />
      <Route path="/signup" element={<SignupBlocked />} />

      {/* Super admin */}
      <Route path="/admin" element={<ProtectedRoute roles={["super_admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute roles={["super_admin"]}><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/domains" element={<ProtectedRoute roles={["super_admin"]}><AdminDomains /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={["super_admin"]}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/onboard" element={<ProtectedRoute roles={["super_admin"]}><AdminOnboardWizard /></ProtectedRoute>} />
      <Route path="/admin/tenants/:id" element={<ProtectedRoute roles={["super_admin"]}><AdminTenantDetail /></ProtectedRoute>} />

      {/* Tenant admin */}
      <Route path="/studio" element={<ProtectedRoute roles={["tenant_admin"]}><StudioDashboard /></ProtectedRoute>} />
      <Route path="/studio/catalogue" element={<ProtectedRoute roles={["tenant_admin"]}><StudioCatalogue /></ProtectedRoute>} />
      <Route path="/studio/catalogue/new" element={<ProtectedRoute roles={["tenant_admin"]}><StudioCatalogueEditor /></ProtectedRoute>} />
      <Route path="/studio/catalogue/:id" element={<ProtectedRoute roles={["tenant_admin"]}><StudioCatalogueEditor /></ProtectedRoute>} />
      <Route path="/studio/plans" element={<ProtectedRoute roles={["tenant_admin"]}><StudioPlans /></ProtectedRoute>} />
      <Route path="/studio/subscribers" element={<ProtectedRoute roles={["tenant_admin"]}><StudioSubscribers /></ProtectedRoute>} />
      <Route path="/studio/billing" element={<ProtectedRoute roles={["tenant_admin"]}><StudioBilling /></ProtectedRoute>} />
      <Route path="/studio/analytics" element={<ProtectedRoute roles={["tenant_admin"]}><StudioAnalytics /></ProtectedRoute>} />
      <Route path="/studio/revenue" element={<ProtectedRoute roles={["tenant_admin"]}><StudioRevenue /></ProtectedRoute>} />
      <Route path="/studio/domains" element={<ProtectedRoute roles={["tenant_admin"]}><StudioDomains /></ProtectedRoute>} />

      {/* Storefront — public */}
      <Route path="/s/:slug" element={<Storefront />} />
      <Route path="/s/:slug/login" element={<StorefrontLogin />} />
      <Route path="/s/:slug/signup" element={<StorefrontSignup />} />
      <Route path="/s/:slug/title/:contentSlug" element={<ContentDetail />} />
      <Route path="/s/:slug/search" element={<SearchResults />} />
      <Route path="/s/:slug/plans" element={<StorefrontPlans />} />
      <Route path="/s/:slug/home" element={<SubscriberHome />} />
      <Route path="/s/:slug/reels" element={<ReelsFeed />} />
      <Route path="/s/:slug/r/:reelSlug" element={<ReelShare />} />

      {/* Storefront — auth required */}
      <Route path="/s/:slug/select-profile" element={<SelectProfile />} />
      <Route path="/s/:slug/profiles" element={<ManageProfiles />} />
      <Route path="/s/:slug/my-list" element={<MyList />} />
      <Route path="/s/:slug/account" element={<Account />} />
      <Route path="/s/:slug/account/billing" element={<AccountBilling />} />
      <Route path="/s/:slug/checkout" element={<Checkout />} />
      <Route path="/s/:slug/checkout/mock" element={<CheckoutMock />} />
      <Route path="/s/:slug/watch/:contentSlug" element={<Watch />} />
      <Route path="/s/:slug/watch/:contentSlug/:season/:episode" element={<Watch />} />

      <Route path="/forbidden" element={<Forbidden />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function AppRoutes() {
  const host = useHostTenant();
  if (!host.ready) return <DomainLoading />;
  if (host.mode === "custom") return <CustomDomainRoutes />;
  if (host.mode === "custom-unresolved") return <DomainNotConnected host={typeof window !== "undefined" ? window.location.hostname : ""} />;
  return <PlatformRoutes />;
}

function App() {
  return (
    <div className="App">
      <HostTenantProvider>
        <AuthProvider>
          <ProfileProvider>
            <BrowserRouter>
              <Toaster
                theme="dark"
                position="top-right"
                toastOptions={{
                  style: {
                    background: "#121212",
                    border: "1px solid #262626",
                    color: "#f8fafc",
                    borderRadius: 0,
                  },
                }}
              />
              <AppRoutes />
            </BrowserRouter>
          </ProfileProvider>
        </AuthProvider>
      </HostTenantProvider>
    </div>
  );
}

export default App;
